// ---------------------------------------------------------------------------
// Generated variant approach — mirrors button/theme.ts
//
// Each intent defines its token references once.
// Each hierarchy rule composes container/button styles from those tokens.
// Canonical variants are produced via Object.fromEntries — no hand-written
// duplication; adding a new intent or hierarchy is a 1-line change.
// ---------------------------------------------------------------------------

interface IntentTokens {
  feedbackSubtle: string // container background
  feedbackSolid: string // container border + text color
  dismissColor: string // dismiss button color (= feedbackSolid for all intents; Q8 removed the success exception)
}

/** Token references per semantic intent. */
const intentTokens: Record<string, IntentTokens> = {
  info: {
    feedbackSubtle: 'var(--vui-feedback-info-subtle)',
    feedbackSolid: 'var(--vui-feedback-info-solid)',
    dismissColor: 'var(--vui-feedback-info-solid)',
  },
  danger: {
    feedbackSubtle: 'var(--vui-feedback-danger-subtle)',
    feedbackSolid: 'var(--vui-feedback-danger-solid)',
    dismissColor: 'var(--vui-feedback-danger-solid)',
  },
  warning: {
    feedbackSubtle: 'var(--vui-feedback-warning-subtle)',
    feedbackSolid: 'var(--vui-feedback-warning-solid)',
    dismissColor: 'var(--vui-feedback-warning-solid)',
  },
  success: {
    feedbackSubtle: 'var(--vui-feedback-success-subtle)',
    feedbackSolid: 'var(--vui-feedback-success-solid)',
    // Q8: success now uses feedbackSolid like other intents (no exception).
    dismissColor: 'var(--vui-feedback-success-solid)',
  },
}

/** Variant styles per visual hierarchy, composed from intent tokens. */
const hierarchyRules: Record<string, (t: IntentTokens) => object> = {
  inline: t => ({
    container: {
      bg: t.feedbackSubtle,
      borderColor: t.feedbackSolid,
      color: t.feedbackSolid,
      '--vui-dismiss-fg': t.dismissColor,
    },
  }),
  // Q5: banner drops border by setting border-width to 0; same colors as inline.
  banner: t => ({
    container: {
      bg: t.feedbackSubtle,
      borderColor: t.feedbackSolid,
      color: t.feedbackSolid,
      '--vui-dismiss-fg': t.dismissColor,
      '--vui-notification-border-width': '0',
    },
  }),
}

// ---------------------------------------------------------------------------

const baseStyle = {
  container: {
    borderRadius: 'var(--vui-notification-border-radius, 0)',
    borderWidth: 'var(--vui-notification-border-width, 1px)',
    bg: 'var(--vui-feedback-neutral-subtle)',
    borderColor: 'var(--vui-feedback-neutral-solid)',
    color: 'var(--vui-feedback-neutral-solid)',
    // Figma: container px:16, py:8, gap:16 between icon | content | dismiss
    px: 'var(--vui-notification-padding-x, 16px)',
    py: 'var(--vui-notification-padding-y, 8px)',
    gap: 'var(--vui-notification-gap, 16px)',
  },
  // Figma: icon and content each get py:8 so total vertical space = 8+8 = 16px from border
  content: {
    py: 'var(--vui-notification-content-padding-y, 8px)',
  },
  button: {},
  icon: {
    py: 'var(--vui-notification-content-padding-y, 8px)',
  },
  text: {
    flex: '1',
    fontSize: '16px',
    lineHeight: '150%',
    fontWeight: 400,
    minW: 0,
  },
  title: {
    mb: '4px',
  },
  // Figma: gap from text to action is 12px; outer spacing handled by container
  actionBox: {
    mt: 'var(--vui-notification-action-margin-top, 12px)',
  },
}

const parts = ['container', 'content', 'button', 'icon', 'text', 'title', 'actionBox']

const sizes = {}

// Generated: inlineInfo, inlineDanger, inlineWarning, inlineSuccess,
//            bannerInfo, bannerDanger, bannerWarning, bannerSuccess
// Legacy aliases below preserve backward compatibility for the old single-key API.
const generatedVariants = Object.fromEntries(
  Object.entries(intentTokens).flatMap(([intentKey, tokens]) =>
    Object.entries(hierarchyRules).map(([hierarchyKey, buildVariant]) => [
      `${hierarchyKey}${intentKey.charAt(0).toUpperCase()}${intentKey.slice(1)}`,
      buildVariant(tokens),
    ]),
  ),
)

const variants = {
  ...generatedVariants,
  // Legacy aliases — kept for backward compatibility
  subtleBlue: generatedVariants.inlineInfo,
  subtleRed: generatedVariants.inlineDanger,
  subtleYellow: generatedVariants.inlineWarning,
  subtleGreen: generatedVariants.inlineSuccess,
  bannerBlue: generatedVariants.bannerInfo,
  bannerRed: generatedVariants.bannerDanger,
  bannerYellow: generatedVariants.bannerWarning,
  bannerGreen: generatedVariants.bannerSuccess,
}

export default {
  baseStyle,
  parts,
  sizes,
  variants,
}
